import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, BellRing, Send } from 'lucide-react';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { DataTable, type Column } from '../components/data-table';

interface AlertRow {
  readonly id: string;
  readonly title: string;
  readonly summary: string | null;
  readonly severityTier: number;
  readonly sourceApi: string;
  readonly isActive: boolean;
  readonly pushSent: boolean;
  readonly issuedAt: string;
  readonly expiresAt: string | null;
  readonly geographicArea: readonly string[];
  readonly language: string | null;
}

interface Response {
  readonly rows: readonly AlertRow[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly kpis: {
    readonly activeCount: number;
    readonly issued24h: number;
    readonly pushFanout: { readonly sent: number; readonly received: number };
  };
}

const PAGE_SIZE = 50;
const fmtInt = new Intl.NumberFormat('en-US');

function severityChip(tier: number) {
  if (tier >= 3) return <span className="nw-chip nw-chip--error">tier {tier}</span>;
  if (tier === 2) return <span className="nw-chip nw-chip--warning">tier {tier}</span>;
  return <span className="nw-chip nw-chip--info">tier {tier}</span>;
}

const columns: readonly Column<AlertRow>[] = [
  {
    key: 'title',
    header: 'Title',
    render: (r) => (
      <span style={{ fontWeight: 500 }} title={r.summary ?? undefined}>
        {r.title}
      </span>
    ),
  },
  {
    key: 'sourceApi',
    header: 'Source',
    width: '120px',
    render: (r) => <span className="nw-chip">{r.sourceApi}</span>,
  },
  {
    key: 'severity',
    header: 'Severity',
    width: '110px',
    render: (r) => severityChip(r.severityTier),
  },
  {
    key: 'geographicArea',
    header: 'Region',
    width: '160px',
    render: (r) =>
      r.geographicArea.length > 0 ? (
        <span style={{ color: 'var(--text-secondary)' }}>
          {r.geographicArea.slice(0, 2).join(', ')}
          {r.geographicArea.length > 2 ? `…+${r.geographicArea.length - 2}` : ''}
        </span>
      ) : (
        '—'
      ),
  },
  {
    key: 'pushSent',
    header: 'Push',
    width: '80px',
    render: (r) =>
      r.pushSent ? (
        <span className="nw-chip nw-chip--success">sent</span>
      ) : (
        <span className="nw-chip">—</span>
      ),
  },
  {
    key: 'isActive',
    header: 'Active',
    width: '90px',
    render: (r) =>
      r.isActive ? (
        <span className="nw-chip nw-chip--info">active</span>
      ) : (
        <span className="nw-chip">expired</span>
      ),
  },
  {
    key: 'issuedAt',
    header: 'Issued',
    width: '160px',
    render: (r) => (
      <span title={r.issuedAt} style={{ color: 'var(--text-secondary)' }}>
        {formatDistanceToNow(new Date(r.issuedAt), { addSuffix: true })}
      </span>
    ),
  },
];

function Kpi({
  label,
  value,
  Icon,
}: {
  readonly label: string;
  readonly value: number;
  readonly Icon: typeof BellRing;
}) {
  return (
    <div className="nw-kpi">
      <div className="nw-kpi__head">
        <span className="nw-kpi__icon">
          <Icon size={18} strokeWidth={2} />
        </span>
        <span className="nw-kpi__label">{label}</span>
      </div>
      <div className="nw-kpi__value">{fmtInt.format(value)}</div>
    </div>
  );
}

export function AlertsList() {
  const [page, setPage] = useState(1);
  const [active, setActive] = useState<'' | 'true' | 'false'>('');

  const params = { page, pageSize: PAGE_SIZE, active };
  const query = useQuery({
    queryKey: queryKeys.alerts(),
    queryFn: async () => {
      const search = new URLSearchParams();
      search.set('page', String(page));
      search.set('pageSize', String(PAGE_SIZE));
      if (active) search.set('active', active);
      const res = await apiClient.get<Response>(
        `/owner/alerts?${search.toString()}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
  // mark unused params var so linter is calm; we read params via query function
  void params;

  const data = query.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Alerts</h1>
        <p className="nw-page-header__subtitle">
          Government emergency alerts ingested from SMHI / Polisen / FEMA. Push fan-out shows how many were sent.
        </p>
      </header>

      {data && (
        <div className="nw-kpi-grid">
          <Kpi label="Active now" value={data.kpis.activeCount} Icon={AlertTriangle} />
          <Kpi label="Issued 24h" value={data.kpis.issued24h} Icon={BellRing} />
          <Kpi label="Push sent 24h" value={data.kpis.pushFanout.sent} Icon={Send} />
        </div>
      )}

      <div className="nw-filters">
        <select
          className="nw-filters__input"
          value={active}
          onChange={(e) => {
            setPage(1);
            setActive(e.target.value as '' | 'true' | 'false');
          }}
        >
          <option value="">All alerts</option>
          <option value="true">Active only</option>
          <option value="false">Expired only</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.rows ?? []}
        rowKey={(r) => r.id}
        total={data?.total}
        page={data?.page ?? page}
        pageSize={data?.pageSize ?? PAGE_SIZE}
        onPageChange={setPage}
        loading={query.isLoading || query.isFetching}
        error={query.error ? (query.error as Error).message : null}
        emptyMessage="No alerts in this window."
      />
    </>
  );
}
